<template>
  <InfuseModal :title="t('title')" @close="emit('close')">
    <form id="start-transaction" class="flex flex-col gap-4" @submit.prevent="onSubmit">
      <InfuseField size="small" :label="t('tokenId.label')">
        <InfuseInput v-model="tokenId" ref="tokenIdInput" :placeholder="t('tokenId.placeholder')" />
      </InfuseField>

      <InfuseField v-if="needsTokenType()" size="small" :label="t('tokenType.label')">
        <InfuseSelect
          name="tokenType"
          size="medium"
          v-model="tokenType"
          :options="tokenTypes"
          :label="t('tokenType.label')"
          :required="needsTokenType()"
        />
      </InfuseField>
    </form>

    <template #actions>
      <InfuseButton
        data-testid="modal-cancel-button"
        color="secondary"
        type="button"
        form="start-transaction"
        @click="emit('close')"
      >
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton data-testid="modal-confirm-button" type="submit" form="start-transaction" :disabled="!tokenId">
        {{ t('action.start') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseSelect } from '@infuse/ui/components';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TokenType } from '@infuse/api/cpms/operator';
import { ChargingStation, Protocol } from '@infuse/cpms/charging-station/store';

interface Props {
  chargingStation: ChargingStation;
  evseId: number;
}

interface Emits {
  (event: 'close'): void;
  (event: 'start', evseId: number, tokenId: string, tokenType?: TokenType): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();

const tokenId = ref<string>();
const tokenIdInput = ref<InstanceType<typeof InfuseInput>>();
const tokenType = ref<TokenType | undefined>(TokenType.ISO_14443);

const tokenTypes = [
  {
    key: 'Central Ad Hoc',
    value: TokenType.CENTRAL_AD_HOC
  },
  {
    key: 'Central App',
    value: TokenType.CENTRAL_APP
  },
  {
    key: 'Central',
    value: TokenType.CENTRAL
  },
  {
    key: 'eMaid',
    value: TokenType.EMAID
  },
  {
    key: 'ISO 14443',
    value: TokenType.ISO_14443
  },
  {
    key: 'ISO 15693',
    value: TokenType.ISO_15693
  },
  {
    key: 'Key Code',
    value: TokenType.KEY_CODE
  },
  {
    key: 'Local',
    value: TokenType.LOCAL
  },
  {
    key: 'Mac Address',
    value: TokenType.MAC_ADDRESS
  }
];

watch(tokenIdInput, (i) => {
  if (i) i.focus();
});

function onSubmit() {
  if (tokenId.value) {
    if (!needsTokenType()) {
      tokenType.value = undefined;
    }

    emit('start', props.evseId, tokenId.value, tokenType.value);
  }
}

function needsTokenType() {
  return props.chargingStation.protocol == Protocol.OCPP201 || props.chargingStation.protocol == Protocol.OCPP21;
}
</script>

<i18n>
{
  "en": {
    "title": "Start transaction",
    "action": {
      "cancel": "Cancel",
      "start": "Start transaction"
    },
    "tokenId": {
      "label": "Token",
      "placeholder": "Token"
    },
    "tokenType": {
      "label": "Token type"
    }
  },
  "nl": {
    "title": "Start transactie",
    "action": {
      "cancel": "Annuleren",
      "start": "Transactie starten"
    },
    "tokenId": {
      "label": "Token",
      "placeholder": "Token"
    },
    "tokenType": {
      "label": "Token type"
    }
  }
}
</i18n>
